import learnHub from '@/content/learnHub.json';
import workProjects from '@/content/workProjects.json';

/**
 * Single source of truth for the /learn course.
 * Keep aligned with:
 *   - `backend/constants/publicGuidesSitemapPaths.js` (LEARN_PATHS)
 *   - `frontend/lib/docsLearnStructuredData.js` (LEARN_GUIDE_PAGES)
 *   - `backend/scripts/seed/seed_guide_pages.json`
 */

export const LEARN_HUB = learnHub.hub;

export const LEARN_FAQS = learnHub.faqs;

export const LEARN_OUTCOMES = workProjects.map(project => ({
  title: project.title, copy: project.description, href: `/learn/${project.slug}`, icon: project.icon,
}));

export const LEARN_MODULES = [
  {
    id: 'basics',
    number: '01',
    navTitle: 'Module 1: AI Basics',
    title: 'AI Basics',
    description: 'What this technology actually is, and how Gab AI is different from a single-model chatbot.',
    color: '#0D5C47',
    icon: 'brain',
    minutes: 12,
    lessons: [
      { slug: 'what-is-ai', label: 'What is AI?', icon: 'brain', minutes: 5 },
      { slug: 'how-gab-ai-works', label: 'How Gab AI Works', icon: 'robot', minutes: 7 },
    ],
  },
  {
    id: 'prompting',
    number: '02',
    navTitle: 'Module 2: Prompting 101',
    title: 'Prompting 101',
    description: 'The three ingredients of a useful prompt — and how to stop getting generic answers.',
    color: '#178A68',
    icon: 'message',
    minutes: 18,
    lessons: [
      { slug: 'first-prompt', label: 'Your First Prompt', icon: 'message', minutes: 5 },
      { slug: 'being-specific', label: 'Being Specific', icon: 'bullseye', minutes: 6 },
      { slug: 'providing-context', label: 'Providing Context', icon: 'layer-group', minutes: 7 },
    ],
  },
  {
    id: 'advanced',
    number: '03',
    navTitle: 'Module 3: Advanced Techniques',
    title: 'Advanced Techniques',
    description: 'Roles, step-by-step reasoning, and examples — the three moves that separate beginners from people who get what they asked for.',
    color: '#D97706',
    icon: 'wand-magic-sparkles',
    minutes: 16,
    lessons: [
      { slug: 'role-prompting', label: 'Role-Based Prompting', icon: 'user-tie', minutes: 5 },
      { slug: 'chain-of-thought', label: 'Chain of Thought', icon: 'link', minutes: 5 },
      { slug: 'few-shot-examples', label: 'Few-Shot Examples', icon: 'list-ol', minutes: 6 },
    ],
  },
  {
    id: 'creating',
    number: '04',
    navTitle: 'Module 4: Creating with AI',
    title: 'Creating with AI',
    description: 'Images, video, music, writing, code, and research — all from the same composer.',
    color: '#B45309',
    icon: 'image',
    minutes: 42,
    lessons: [
      { slug: 'text-to-image', label: 'Text to Image', icon: 'image', minutes: 6 },
      { slug: 'image-to-video', label: 'Image to Video', icon: 'film', minutes: 6 },
      { slug: 'video-generation', label: 'Video Generation', icon: 'video', minutes: 6 },
      { slug: 'music-generation', label: 'Music Generation', icon: 'music', minutes: 6 },
      { slug: 'writing-assistant', label: 'Writing with AI', icon: 'pen-fancy', minutes: 6 },
      { slug: 'code-help', label: 'Code Assistance', icon: 'code', minutes: 7 },
      { slug: 'research', label: 'Research & Analysis', icon: 'magnifying-glass', minutes: 5 },
    ],
  },
  {
    id: 'power',
    number: '05',
    navTitle: 'Module 5: Power Features',
    title: 'Power Features',
    description: 'Agents that remember the brief, files the model can actually read, and memory that persists overnight.',
    color: '#0A3F32',
    icon: 'rocket',
    minutes: 28,
    lessons: [
      { slug: 'custom-agents', label: 'Custom Agents', icon: 'robot', minutes: 7 },
      { slug: 'collections-org', label: 'Collections', icon: 'folder', minutes: 5 },
      { slug: 'file-uploads', label: 'File Uploads', icon: 'paperclip', minutes: 6 },
      { slug: 'memory', label: 'AI Memory', icon: 'brain', minutes: 5 },
      { slug: 'voice-and-search', label: 'Voice & Web Search', icon: 'microphone', minutes: 5 },
    ],
  },
  {
    id: 'practice',
    number: '06',
    navTitle: 'Module 6: Best Practices',
    title: 'Best Practices',
    description: 'Treat the first reply as a draft. Then stop making the six mistakes that waste most people’s credits.',
    color: '#0D5C47',
    icon: 'shield-halved',
    minutes: 12,
    lessons: [
      { slug: 'iterating-prompts', label: 'Iterating Prompts', icon: 'rotate', minutes: 6 },
      { slug: 'common-mistakes', label: 'Common Mistakes', icon: 'triangle-exclamation', minutes: 6 },
    ],
  },
  {
    id: 'agentic',
    number: '07',
    navTitle: 'Module 7: Your Work',
    title: 'Your Work',
    description: 'Brief Gab AI Work, open its artifacts, and turn feedback into a finished result.',
    color: '#123524',
    icon: 'briefcase',
    minutes: 32,
    lessons: [
      { slug: 'agentic-workflows', label: 'Meet Gab AI Work', icon: 'briefcase', minutes: 8 },
      { slug: 'agent-tools', label: 'Your artifact toolkit', icon: 'layer-group', minutes: 8 },
      { slug: 'briefing-agents', label: 'Write a useful Work brief', icon: 'list-check', minutes: 8 },
      { slug: 'running-agent-loops', label: 'Review, revise, and finish', icon: 'arrows-rotate', minutes: 8 },
    ],
  },
  {
    id: 'projects', number: '08', navTitle: 'Module 8: Work Projects', title: 'Work Projects',
    description: 'Six hands-on projects. Make an artifact, refine it, and take the finished file with you.',
    color: '#123524', icon: 'layer-group',
    minutes: workProjects.reduce((sum, project) => sum + project.minutes, 0),
    lessons: workProjects.map(project => ({ slug: project.slug, label: project.title, icon: project.icon, minutes: project.minutes })),
  },
];

export const LEARN_LESSONS = LEARN_MODULES.flatMap((mod) =>
  mod.lessons.map((lesson) => ({
    ...lesson,
    href: `/learn/${lesson.slug}`,
    category: mod.title,
    moduleId: mod.id,
    moduleColor: mod.color,
  })),
);

export const LEARN_LESSON_COUNT = LEARN_LESSONS.length;

export const LEARN_TOTAL_MINUTES = LEARN_MODULES.reduce((sum, mod) => sum + mod.minutes, 0);

export function getLearnStructure() {
  return [
    {
      title: 'Getting Started',
      items: [{ slug: 'index', label: 'Learn Hub', icon: 'graduation-cap' }],
    },
    ...LEARN_MODULES.map((mod) => ({
      title: mod.navTitle,
      items: mod.lessons.map((lesson) => ({
        slug: lesson.slug,
        label: lesson.label,
        icon: lesson.icon,
      })),
    })),
  ];
}

export function getLessonMeta(slug) {
  const s = String(slug || '').toLowerCase();
  for (const mod of LEARN_MODULES) {
    const index = mod.lessons.findIndex((lesson) => lesson.slug === s);
    if (index === -1) continue;
    return {
      module: mod,
      lesson: mod.lessons[index],
      index: index + 1,
      total: mod.lessons.length,
      labels: mod.lessons.map((lesson) => lesson.label),
    };
  }
  return null;
}

export function getLearnGuidePages() {
  return [
    { path: '/learn', label: 'Learn Hub' },
    ...LEARN_LESSONS.map((lesson) => ({ path: lesson.href, label: lesson.label })),
  ];
}

export function getLearnPaths() {
  return ['/learn', ...LEARN_LESSONS.map((lesson) => lesson.href)];
}
